/**
 * ONE-TIME BOOT SWEEP for the localStorage blobs T651 M7 orphaned.
 *
 * ⛔ WHY THIS FILE EXISTS AT ALL (M7 security review, finding 2). M7 deleted
 * client/lib/nursingHomeCaptureStore.ts, because the nursing capture is a
 * `booking_series` row now instead of a per-browser blob. That deletion took
 * `deleteNursingHomeCapture` with it — THE ONLY CODE THAT COULD EVER REMOVE A
 * KEY ALREADY WRITTEN. The M7 diff's own note, "the eviction has no successor
 * and needs none", is true of FUTURE WRITES and false of existing ones: every
 * `ecmb-nh-capture:<instanceId>:<bookingId>` key a kiosk browser had already
 * saved would have sat there for ever, readable by nothing and erasable by
 * nothing.
 *
 * ⚠️ THIS IS REAL PERSONAL DATA, NOT DEBRIS. Each blob holds the free-text
 * DESTINATION (routinely a named hospital or clinic), the ACCOUNT ID (which
 * resolves to the home) and a WHEELCHAIR COUNT — a disability signal under
 * Article 9 — about nursing-home residents, in a SHARED OFFICE BROWSER, in the
 * EU. "Nobody reads it any more" is not an erasure path; deleting the reader
 * while leaving the data is the worst of both, because the data survives with
 * no mechanism and no owner.
 *
 * ⭐ PREFIX-SCOPED, AND DELIBERATELY *NOT* instanceId-SCOPED. The dead store
 * namespaced its keys by the server's per-database instanceId (T414 F4), so a
 * browser that had pointed at more than one database holds keys under several.
 * Since NOTHING reads any of them now, the honest sweep is every key under the
 * prefix, not just this database's — an instanceId filter would leave the
 * orphans of every other database behind, which is precisely the gap being
 * closed. It also lets this run before the registry has loaded.
 *
 * ⛔ COLLECT THE KEYS FIRST, THEN REMOVE. `localStorage.key(i)` indexes a LIVE
 * collection: removing inside the ascending loop shifts every later key down
 * one and the scan silently skips every second match. Two passes, no cleverness.
 *
 * ⚠️ IDEMPOTENT AND BEST-EFFORT, in that order. It runs on every boot (there is
 * no "have I swept?" flag — a flag would itself be a key to keep, and a no-match
 * scan of a handful of keys is free), and storage can be unavailable in private
 * mode or over quota, which must never stop the kiosk from starting.
 *
 * This file has a SINK_ALLOW entry in scripts/check-copy.sh — the only client
 * file permitted to touch localStorage. It is deliberately a separate module
 * with a unique basename rather than a few lines in index.tsx: the allowlist is
 * keyed by BASENAME, and exempting `index.tsx` would exempt any future
 * index.tsx too. Everything the exemption covers is in these few lines.
 */

/** The dead store's key prefix (nursingHomeCaptureStore.ts's KEY_PREFIX,
 *  preserved here because the file it came from no longer exists). */
const LEGACY_KEY_PREFIX = 'ecmb-nh-capture:';

/** How many keys were removed. Returned for the pin; no caller uses it. */
export function purgeLegacyNursingCaptures(): number {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key !== null && key.startsWith(LEGACY_KEY_PREFIX)) doomed.push(key);
    }
    for (const key of doomed) window.localStorage.removeItem(key);
    return doomed.length;
  } catch {
    /* storage unavailable - the kiosk still boots */
    return 0;
  }
}
